import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { BaseVideoPlayer } from '@/app/components/shared/VideoPlayer/BaseVideoPlayer';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { SITE } from '@/app/constants';
import { reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { extractMessageText, isYoloLikeMessageList, mergeConsecutiveAssistantMessages } from '../../_utils';
import { clawsGetReplaySession } from '../../apis';
import type { ChatMessageItem, ClawV1CaseInfo } from '../../types';
import { ClawMarkdown } from '../ClawMarkdown';

const CaseLoading = () => {
  return (
    <div className="flex animate-pulse flex-col gap-2">
      <div className="mb-1 h-5 w-2/3 rounded-md bg-system-white08" />
      <div className="mb-3 flex justify-end">
        <div className="h-10 w-4/5 rounded-16 bg-system-white08" />
      </div>
      <div className="space-y-2">
        <div className="h-4 w-full rounded-md bg-system-white08" />
        <div className="h-4 w-full rounded-md bg-system-white08" />
        <div className="h-4 w-3/4 rounded-md bg-system-white08" />
      </div>
    </div>
  );
};

interface CaseDialogProps {
  open: boolean;
  onClose: () => void;
  caseInfo?: ClawV1CaseInfo;
}

export const CaseDialog = ({ open, onClose, caseInfo }: CaseDialogProps) => {
  const t = useTranslations();
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);

  const pathname = usePathname();

  useEffect(() => {
    if (!open) {
      setIsVideoLoading(false);
      return;
    }
    setIsVideoLoading(!!caseInfo?.video_url);
  }, [open, caseInfo?.video_url]);

  useEffect(() => {
    if (!open || !caseInfo?.session_id) {
      setMessages([]);
      return;
    }

    setIsLoading(true);
    clawsGetReplaySession({
      query: {
        session_id: caseInfo.session_id,
        'pager.pagesz': 20,
        'pager.direction': 'forward',
        role: ['assistant', 'user'],
      },
    })
      .then(({ data }) => {
        const rawMessages = data?.messages ?? [];

        const parsed = rawMessages
          .map((msg) => extractMessageText(msg, { failedFallbackMessage: '' }))
          .filter((msg): msg is ChatMessageItem => msg !== null);

        // yolo 会话的 case replay 同样按"独立消息"渲染，避免分镜卡之后的视频/完成提示
        // 被合并到一条气泡里被吞掉。case 接口未透出 mode，按消息体推断。
        const merged = isYoloLikeMessageList(parsed) ? parsed : mergeConsecutiveAssistantMessages(parsed);

        // 截取前两轮对话（遇到第三条 user 消息时停止）
        let userCount = 0;
        let cutIndex = merged.length;
        for (let i = 0; i < merged.length; i++) {
          if (merged[i].role === 'user') {
            userCount++;
            if (userCount === 3) {
              cutIndex = i;
              break;
            }
          }
        }

        setMessages(merged.slice(0, cutIndex));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, caseInfo?.session_id]);

  const videoWidth = caseInfo?.video_width ?? 16;
  const videoHeight = caseInfo?.video_height ?? 9;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        inheritTheme
        className="flex h-[700px] w-auto max-w-none items-center justify-center gap-4 overflow-hidden rounded-3xl bg-[#17191B] light:bg-system-bg02 p-0 shadow-[0_20px_60px_0_rgba(0,0,0,0.32)] sm:top-0 sm:left-0 sm:h-[100dvh] sm:w-full sm:max-w-none sm:translate-x-0 sm:translate-y-0 sm:flex-col sm:items-stretch sm:justify-start sm:gap-0 sm:rounded-none sm:border-0 sm:pt-[env(safe-area-inset-top)] sm:pb-[env(safe-area-inset-bottom)]"
        closeClassName="top-6 right-6 text-system-white80 hover:text-system-white sm:top-[calc(env(safe-area-inset-top)+16px)] sm:right-4 sm:z-10"
      >
        <div className="relative flex h-full w-[700px] shrink-0 items-center justify-center p-3 sm:h-auto sm:max-h-[52dvh] sm:w-full sm:shrink-0 sm:items-start sm:p-4 sm:pt-14">
          {caseInfo?.video_url ? (
            <MediaWithWatermark
              width={videoWidth}
              height={videoHeight}
              showWatermark={SITE === 'china'}
              className="overflow-hidden rounded-16 sm:max-h-[calc(52dvh-72px)] sm:max-w-full sm:rounded-12"
            >
              <BaseVideoPlayer
                src={caseInfo.video_url}
                poster={caseInfo.cover_url}
                className="size-full"
                hasAudio
                defaultVolume={0.5}
                initShowControls
                autoPlay
                onReady={() => setIsVideoLoading(false)}
                onError={() => setIsVideoLoading(false)}
              />
            </MediaWithWatermark>
          ) : (
            <div className="h-full w-full overflow-hidden rounded-16 bg-system-white08 sm:aspect-video sm:h-auto sm:rounded-12" />
          )}
          {isVideoLoading && (
            <div className="absolute inset-3 animate-pulse rounded-16 bg-system-white08 sm:inset-4 sm:top-14 sm:rounded-12" />
          )}
        </div>
        <div className="flex h-full w-[400px] shrink-0 flex-col items-center justify-between p-6 pt-14 pl-3 sm:min-h-0 sm:w-full sm:flex-1 sm:p-4 sm:pt-0">
          {caseInfo?.name && (
            <div className="mb-3 w-full shrink-0 pr-10 font-semibold text-base text-system-white sm:pr-0">
              {caseInfo.name}
            </div>
          )}
          <div className="min-h-0 w-full flex-1 overflow-y-auto">
            {isLoading ? (
              <CaseLoading />
            ) : (
              <div className="flex flex-col gap-2">
                {messages.map((msg) =>
                  msg.role === 'user' ? (
                    <div key={msg.id} className="mb-3 flex justify-end">
                      <div className="w-fit whitespace-pre-line rounded-16 bg-[rgba(87,101,242,0.16)] px-4 py-3 text-sm">
                        {msg.content}
                      </div>
                    </div>
                  ) : (
                    <ClawMarkdown key={msg.id} content={msg.content ?? ''} className="mb-3" />
                  ),
                )}
              </div>
            )}
          </div>

          <div className="flex w-full shrink-0 justify-end gap-2 pt-2 sm:pt-3">
            <Button
              variant="solid"
              className="h-11 w-full rounded-12 bg-system-white light:bg-[#5765F2] font-medium light:text-white text-black text-sm hover:bg-[#E6E6E6] light:hover:bg-[#5765F2]/90 sm:h-12"
              onClick={() => {
                reportEvent('claw_element_click', {
                  element_name: 'view_session_replay',
                  from_page: pathname,
                  case_id: caseInfo?.id || '',
                });
                openOrRedirect(`/claw/share-session/${caseInfo?.session_id}`);
              }}
            >
              {t('claw.casesDialog.viewReplay')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
